import { useCallback, useState } from 'react';
import {
  retainTreePackagePicks,
  treeZoneKey,
  withTreePackage,
  type InventoryTreeLeafRef,
  type UpdatedContextRow,
} from './tree-leaves';

interface TreePackagePicks {
  /** Zone id → the package picked from the tree, by `inventoryFormationPackageId`. */
  picks: Record<string, number>;
  /** A package node was clicked: put its formation on it and select `leaf` there. */
  selectPackage: (
    leaf: InventoryTreeLeafRef,
    inventoryFormationPackageId: number
  ) => void;
  /** An add/remove apply landed: drop the picks it replaces, return the rest. */
  retain: (
    included: ReadonlySet<string>,
    rows: readonly UpdatedContextRow[]
  ) => Record<string, number>;
}

/**
 * The packages picked from the tree's own package nodes.
 *
 * Frontend-only, like the add/remove picker's choices, and applied over them:
 * a tree click is the later choice. A pick persists, so a later click on the
 * formation's type curve stays on the chosen package, and the gun barrel and
 * slot table, which read every formation's chosen package, follow it.
 *
 * @param {Function} setSelectedLeaf - The page's selection setter.
 * @return {TreePackagePicks} The picks and their handlers.
 */
export function useTreePackagePicks(
  setSelectedLeaf: (leaf: InventoryTreeLeafRef) => void
): TreePackagePicks {
  const [picks, setPicks] = useState<Record<string, number>>({});

  const selectPackage = useCallback(
    (leaf: InventoryTreeLeafRef, inventoryFormationPackageId: number) => {
      setPicks((prev) => ({
        ...prev,
        [treeZoneKey(leaf.typeCurveArea, leaf.formation)]:
          inventoryFormationPackageId,
      }));
      setSelectedLeaf(withTreePackage(leaf, inventoryFormationPackageId));
    },
    [setSelectedLeaf]
  );

  const retain = useCallback(
    (included: ReadonlySet<string>, rows: readonly UpdatedContextRow[]) => {
      const kept = retainTreePackagePicks(picks, included, rows);
      setPicks(kept);
      return kept;
    },
    [picks]
  );

  return { picks, selectPackage, retain };
}
